import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { CreatePaymentDto, PaymentDto, PaymentStatusResponse } from '../types/payment';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7245/api';

interface LoginCredentials {
  email: string;
  password: string;
}

interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword?: string;
  role: string;
  phoneNumber?: string;
  referralCode?: string;
}

interface AuthResponse {
  success: boolean;
  message: string;
  data: {
    Id: string;
    FirstName: string;
    LastName: string;
    Email: string;
    Token: string;
    TokenExpiration: string;
    UserType: string;
    IsEmailConfirmed: boolean;
    ProfileImageUrl?: string;
  };
}

interface AvailabilitySlot {
  id: string;
  instructorId: string;
  startTime: string;
  endTime: string;
  isBooked: boolean;
  studentId?: string;
  status?: string;
  studentName?: string;
  lessonId?: number;
}

interface StudentProgress {
  studentId: string;
  studentName: string;
  studentEmail: string;
  phoneNumber?: string;
  studentNumber?: string;
  licenseNumber?: string;
  licenseExpiryDate?: string;
  registrationDate?: string;
  totalLessons: number;
  completedLessons: number;
  progressPercentage: number;
  coveredDvsaTopicsCount?: number;
  licenseFrontImagePath?: string;
  licenseBackImagePath?: string;
  licenseStatus?: string;
  licenseRejectionReason?: string;
  theoryTestPassDate?: string;
  instructorId?: number;
  instructorName?: string;
  theoryTestPassReference?: string;
  dvlaShareCode?: string;
  eyesightTestPassed?: boolean;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  medicalConditions?: string;
  transmissionType?: string;
  lessons: Array<{
    lessonId: string;
    title: string;
    description: string;
    isCompleted: boolean;
    completionDate: string | null;
    notes: string | null;
    topicId?: number;
    topicName?: string;
  }>;
}

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include auth token
    this.client.interceptors.request.use(
      (config) => {
        console.log('Making request to:', (config.baseURL || '') + (config.url || ''));
        console.log('Request config:', config);
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    // Add response interceptor for debugging and auth handling
    this.client.interceptors.response.use(
      (response) => {
        console.log('Response received:', response);
        return response;
      },
      (error) => {
        console.error('Response interceptor error:', error);
        console.error('Error config:', error.config);
        console.error('Error response:', error.response);
        
        if (error.response && error.response.status === 401) {
          console.warn('Unauthorized request detected, clearing session.');
          localStorage.removeItem('token');
          // Only redirect if we are not already on login/register pages to avoid loops
          if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/register')) {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  getImageUrl(path: string | null): string {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    const baseUrl = this.client.defaults.baseURL || '';
    const token = localStorage.getItem('token');
    const tokenParam = token ? `&token=${encodeURIComponent(token)}` : '';
    return `${baseUrl}/upload/files?path=${encodeURIComponent(path)}${tokenParam}`;
  }

  // Generic HTTP methods
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.get<T>(url, config);
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.post<T>(url, data, config);
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.put<T>(url, data, config);
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.delete<T>(url, config);
  }

  // Auth methods
  async login(credentials: LoginCredentials): Promise<AxiosResponse<AuthResponse>> {
    return this.client.post<AuthResponse>('/Auth/login', credentials);
  }

  async register(userData: RegisterData): Promise<AxiosResponse<AuthResponse>> {
    return this.client.post<AuthResponse>('/Auth/register', userData);
  }

  async googleLogin(idToken: string, userType?: string): Promise<AxiosResponse<AuthResponse>> {
    return this.client.post<AuthResponse>('/Auth/google-login', { idToken, userType });
  }

  async confirmEmail(userId: string, token: string): Promise<AxiosResponse> {
    return this.client.get(`/Auth/confirm-email?userId=${encodeURIComponent(userId)}&token=${encodeURIComponent(token)}`);
  }

  async forgotPassword(email: string): Promise<AxiosResponse> {
    return this.client.post('/Auth/forgot-password', { email });
  }

  async resetPassword(resetData: any): Promise<AxiosResponse> {
    return this.client.post('/Auth/reset-password', resetData);
  }

  async getClaimInfo(token: string): Promise<AxiosResponse> {
    return this.client.get(`/Auth/claim-info?token=${encodeURIComponent(token)}`);
  }

  async claimProfile(claimData: any): Promise<AxiosResponse<AuthResponse>> {
    return this.client.post<AuthResponse>('/Auth/claim-profile', claimData);
  }

  async getInstructors(): Promise<AxiosResponse> {
    return this.client.get('/Instructor/list');
  }

  // Instructor methods
  async getInstructorProfile(): Promise<AxiosResponse> {
    return this.client.get('/UserProfile');
  }

  async updateInstructorProfile(profileData: any): Promise<AxiosResponse> {
    return this.client.put('/UserProfile', profileData);
  }

  async getInstructorAvailabilitySlots(): Promise<AxiosResponse<AvailabilitySlot[]>> {
    return this.client.get<AvailabilitySlot[]>('/Instructor/availability-slots');
  }

  async generateTimeSlots(date: string, times: string[]): Promise<AxiosResponse<AvailabilitySlot[]>> {
    return this.client.post<AvailabilitySlot[]>('/Instructor/availability/generate-slots', { date, timeSlots: times });
  }

  async updateAvailabilitySlot(slotId: string, updates: Partial<AvailabilitySlot>): Promise<AxiosResponse<AvailabilitySlot>> {
    return this.client.put<AvailabilitySlot>(`/Instructor/availability-slots/${slotId}`, updates);
  }

  async deleteAvailabilitySlot(slotId: string): Promise<AxiosResponse<void>> {
    return this.client.delete(`/Instructor/availability-slots/${slotId}`);
  }

  async getLessonRequests(): Promise<AxiosResponse> {
    return this.client.get('/Instructor/lesson-requests');
  }

  async approveLesson(lessonId: number): Promise<AxiosResponse> {
    return this.client.post(`/Instructor/lessons/${lessonId}/approve`);
  }

  async rejectLesson(lessonId: number): Promise<AxiosResponse> {
    return this.client.post(`/Instructor/lessons/${lessonId}/reject`);
  }

  async registerStudent(studentData: any): Promise<AxiosResponse> {
    return this.client.post('/Instructor/students', studentData);
  }

  // Student methods
  async getStudentProfile(): Promise<AxiosResponse> {
    return this.client.get('/UserProfile');
  }

  async updateStudentProfile(profileData: any): Promise<AxiosResponse> {
    return this.client.put('/UserProfile', profileData);
  }

  // Student progress methods
  async getStudents(): Promise<AxiosResponse<StudentProgress[]>> {
    return this.client.get<StudentProgress[]>('/instructor/students');
  }

  async getStudentProgress(studentId: string): Promise<AxiosResponse<StudentProgress>> {
    return this.client.get<StudentProgress>(`/instructor/students/${studentId}/progress`);
  }

  async sendMessageToStudents(data: { studentId?: string; message: string; sendEmail: boolean; sendSms: boolean }): Promise<AxiosResponse> {
    return this.client.post('/Instructor/students/send-message', data);
  }

  async updateStudentDetails(
    studentId: string,
    details: any
  ): Promise<AxiosResponse<{ success: boolean; message: string }>> {
    return this.client.put(`/Instructor/students/${studentId}`, details);
  }

  async getTopics(): Promise<AxiosResponse<Array<{ id: number; code: string; name: string; description: string }>>> {
    return this.client.get('/Instructor/topics');
  }

  async scheduleLesson(
    studentId: string,
    lessonData: {
      lessonDate: string;
      duration: number;
      notes?: string;
      topicId?: number;
    }
  ): Promise<AxiosResponse<{ success: boolean; message: string }>> {
    return this.client.post(`/Instructor/students/${studentId}/lessons`, lessonData);
  }

  async updateLessonStatus(
    studentId: string,
    lessonId: string,
    isCompleted: boolean,
    notes?: string,
    topicId?: number
  ): Promise<AxiosResponse<{ success: boolean }>> {
    return this.client.put(`/instructor/students/${studentId}/lessons/${lessonId}`, {
      isCompleted,
      notes,
      topicId
    });
  }

  // Student specific methods
  async bookLesson(bookingData: { instructorId: string; startTime: string; endTime: string; duration: number; notes?: string; topicId?: number; useFreeLessonCredit?: boolean }): Promise<AxiosResponse> {
    return this.client.post('/students/book-slot', bookingData);
  }

  async getStudentLessons(startDate?: string, endDate?: string): Promise<AxiosResponse> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return this.client.get('/students/lessons', { params });
  }

  async getStudentLessonsProgress(): Promise<AxiosResponse> {
    return this.client.get('/students/progress');
  }

  async getMyBadges(): Promise<AxiosResponse> {
    return this.client.get('/students/badges');
  }

  async cancelLesson(lessonId: number): Promise<AxiosResponse<{ success: boolean; message: string }>> {
    return this.client.post<{ success: boolean; message: string }>(`/students/lessons/${lessonId}/cancel`);
  }

  async updateStudentLesson(lessonId: number, data: { topicId?: number; notes?: string }): Promise<AxiosResponse<{ success: boolean; message: string }>> {
    return this.client.put<{ success: boolean; message: string }>(`/students/lessons/${lessonId}`, data);
  }

  // Admin methods
  async getUsers(): Promise<AxiosResponse> {
    return this.client.get('/Admin/users');
  }

  async activateUser(userId: string): Promise<AxiosResponse> {
    return this.client.post(`/Admin/users/${userId}/activate`);
  }

  async deactivateUser(userId: string): Promise<AxiosResponse> {
    return this.client.post(`/Admin/users/${userId}/deactivate`);
  }

  async getAdminDashboard(): Promise<AxiosResponse> {
    return this.client.get('/Admin/dashboard');
  }

  async getInstructorsSubscriptions(): Promise<AxiosResponse> {
    return this.client.get('/Admin/subscriptions');
  }

  async importInstructors(instructors: any[]): Promise<AxiosResponse> {
    return this.client.post('/Admin/instructors/import', instructors);
  }

  async sendClaimEmails(instructorIds: string[]): Promise<AxiosResponse> {
    return this.client.post('/Admin/instructors/send-claim-emails', { instructorIds });
  }

  async updateInstructorSubscription(data: { userId: string; subscriptionType: string; expiryDate?: string; hasWhatsAppAddon: boolean; notes?: string }): Promise<AxiosResponse> {
    return this.client.post('/Admin/subscriptions/update', data);
  }

  async getUserDetails(userId: string): Promise<AxiosResponse> {
    return this.client.get(`/Admin/users/${userId}/details`);
  }

  async adminChangePassword(userId: string, newPassword: string): Promise<AxiosResponse> {
    return this.client.post(`/Admin/users/${userId}/change-password`, { newPassword });
  }

  async adminUpdateUserDetails(userId: string, data: any): Promise<AxiosResponse> {
    return this.client.put(`/Admin/users/${userId}/details`, data);
  }

  // Payment methods
  async createPayment(paymentData: CreatePaymentDto): Promise<AxiosResponse<PaymentDto>> {
    return this.client.post<PaymentDto>('/Payments/create', paymentData);
  }

  async executePayment(paymentId: string, payerId: string): Promise<AxiosResponse<PaymentDto>> {
    return this.client.get<PaymentDto>(`/Payments/execute?paymentId=${paymentId}&PayerID=${payerId}`);
  }

  async getPaymentStatus(paymentId: string): Promise<AxiosResponse<PaymentDto>> {
    return this.client.get<PaymentDto>(`/Payments/status/${paymentId}`);
  }

  async checkSubscriptionStatus(): Promise<AxiosResponse<PaymentStatusResponse>> {
    return this.client.get<PaymentStatusResponse>('/Payments/subscription/active');
  }

  async checkWhatsAppAddonStatus(): Promise<AxiosResponse<{ hasWhatsAppAddon: boolean }>> {
    return this.client.get<{ hasWhatsAppAddon: boolean }>('/Payments/subscription/whatsapp');
  }

  async approvePayment(paymentId: number): Promise<AxiosResponse<PaymentDto>> {
    return this.client.post<PaymentDto>(`/Payments/approve/${paymentId}`);
  }

  async getPendingPayments(): Promise<AxiosResponse<PaymentDto[]>> {
    return this.client.get<PaymentDto[]>('/Payments/pending');
  }

  // Notification methods
  async getNotifications(): Promise<AxiosResponse<any[]>> {
    return this.client.get<any[]>('/Notifications');
  }

  async markNotificationAsRead(id: number): Promise<AxiosResponse<void>> {
    return this.client.post<void>(`/Notifications/${id}/read`);
  }

  async markAllNotificationsAsRead(): Promise<AxiosResponse<void>> {
    return this.client.post<void>('/Notifications/read-all');
  }

  // Password & License management methods
  async changeStudentPassword(studentId: string, newPassword: string): Promise<AxiosResponse> {
    return this.client.post(`/Instructor/students/${studentId}/change-password`, { newPassword });
  }

  async changeSelfPassword(currentPassword: string, newPassword: string): Promise<AxiosResponse> {
    return this.client.post('/students/change-password', { currentPassword, newPassword });
  }

  async uploadLicenseImage(studentId: string, side: 'front' | 'back', file: File): Promise<AxiosResponse<{ path: string }>> {
    const formData = new FormData();
    formData.append('studentId', studentId);
    formData.append('side', side);
    formData.append('file', file);
    return this.client.post('/upload/license', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async uploadProfileImage(file: File): Promise<AxiosResponse<{ path: string }>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.client.post('/upload/profile-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async verifyStudentLicense(studentId: string, status: 'Approved' | 'Rejected', rejectionReason?: string): Promise<AxiosResponse> {
    return this.client.post(`/Instructor/students/${studentId}/verify-license`, { status, rejectionReason });
  }

  // WhatsApp connection methods (Instructor)
  async getWhatsAppSettings(): Promise<AxiosResponse<{ instanceName: string | null; status: string; hasInstance: boolean }>> {
    return this.client.get('/WhatsApp/settings');
  }

  async connectWhatsApp(): Promise<AxiosResponse<{ qrCode: string | null; pairingCode: string | null; status: string }>> {
    return this.client.post('/WhatsApp/connect');
  }

  async disconnectWhatsApp(): Promise<AxiosResponse<{ success: boolean }>> {
    return this.client.post('/WhatsApp/disconnect');
  }

  async deleteWhatsApp(): Promise<AxiosResponse<{ success: boolean }>> {
    return this.client.post('/WhatsApp/delete');
  }

  async sendWhatsAppMessage(studentId: string, message: string): Promise<AxiosResponse<{ success: boolean; message: string }>> {
    return this.client.post('/WhatsApp/send-message', { studentId, message });
  }

  // Marketplace & Search
  async searchInstructors(outcode: string, transmission?: string, maxRate?: number, licenseType?: string, gender?: string, page: number = 1, pageSize: number = 6): Promise<AxiosResponse> {
    const params: any = { outcode, page, pageSize };
    if (transmission) params.transmission = transmission;
    if (maxRate) params.maxRate = maxRate;
    if (licenseType) params.licenseType = licenseType;
    if (gender) params.gender = gender;
    return this.client.get('/Instructors/search', { params });
  }

  async getMyInstructorProfile(): Promise<AxiosResponse> {
    return this.client.get('/Instructors/my-profile');
  }

  async updateMyInstructorProfile(profileData: { 
    bio?: string; 
    baseHourlyRate: number; 
    transmissionType: string; 
    outcodes?: string[];
    licenseTypes?: string[];
    vehicleImageUrls?: string[];
    logoUrl?: string | null;
    gender?: string | null;
    subdomain?: string | null;
    themeName?: string;
  }): Promise<AxiosResponse> {
    return this.client.put('/Instructors/my-profile', profileData);
  }

  async uploadVehicleImage(file: File): Promise<AxiosResponse<{ path: string }>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.client.post('/upload/vehicle-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async getActiveTestSlotAlerts(): Promise<AxiosResponse<any[]>> {
    return this.client.get('/TestSlotAlerts/active');
  }

  async reportTestSlotAlert(data: { testCentreName: string; slotDateTime: string; notes?: string }): Promise<AxiosResponse> {
    return this.client.post('/TestSlotAlerts', data);
  }

  async triggerReengagePupil(data: { studentId: string; message: string }): Promise<AxiosResponse> {
    return this.client.post('/WhatsApp/reengage-pupil', data);
  }

  async scanReceipt(file: File): Promise<AxiosResponse<{ date?: string; amount?: number; category?: string; merchant?: string; notes?: string }>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.client.post('/expenses/scan-receipt', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async submitPublicInquiry(data: {
    instructorId: number;
    fullName: string;
    email: string;
    phone: string;
    message?: string;
    preferredTransmission: string;
    experienceLevel: string;
  }): Promise<AxiosResponse> {
    return this.client.post('/Quotes/public-inquiry', data);
  }

  async getPublicProfile(idOrSubdomain: string): Promise<AxiosResponse<any>> {
    return this.client.get(`/instructors/public/${idOrSubdomain}`);
  }

  // Quotes
  async requestQuote(quoteData: { instructorId: number; message?: string; preferredTransmission: string; experienceLevel: string }): Promise<AxiosResponse> {
    return this.client.post('/Quotes/request', quoteData);
  }

  async getReceivedQuotes(): Promise<AxiosResponse> {
    return this.client.get('/Quotes/received');
  }

  async respondToQuote(id: number, status: 'Accepted' | 'Rejected', responseMessage?: string): Promise<AxiosResponse> {
    return this.client.post(`/Quotes/${id}/respond`, { status, responseMessage });
  }

  // Google Calendar Integration
  async connectGoogleCalendar(): Promise<AxiosResponse<{ url: string }>> {
    return this.client.get('/GoogleCalendar/connect');
  }

  async toggleGoogleCalendarSync(enabled: boolean): Promise<AxiosResponse<{ success: boolean; enabled: boolean }>> {
    return this.client.post('/GoogleCalendar/toggle', { enabled });
  }

  // Mock Theory Test
  async startTheoryExam(): Promise<AxiosResponse<{ sessionId: number; questions: any[] }>> {
    return this.client.get('/TheoryTest/start');
  }

  async submitTheoryExam(sessionId: number, answers: Array<{ questionId: number; selectedOptionId: number }>): Promise<AxiosResponse> {
    return this.client.post('/TheoryTest/submit', { sessionId, answers });
  }

  async getTheoryExamHistory(): Promise<AxiosResponse> {
    return this.client.get('/TheoryTest/history');
  }

  // Expenses Tracker
  async getExpenses(): Promise<AxiosResponse<any[]>> {
    return this.client.get('/Expenses');
  }

  async createExpense(expenseData: { amount: number; category: string; description?: string; expenseDate: string; receiptImageUrl?: string; odometerReading?: number | null; fuelVolumeLitres?: number | null; vehicleRegistration?: string | null }): Promise<AxiosResponse> {
    return this.client.post('/Expenses', expenseData);
  }

  async deleteExpense(id: number): Promise<AxiosResponse> {
    return this.client.delete(`/Expenses/${id}`);
  }

  async getTaxReport(): Promise<AxiosResponse> {
    return this.client.get('/Expenses/report');
  }

  // Reviews & Verified Passes
  async createReview(reviewData: { instructorId: number; rating: number; comment?: string; isPassedPracticalTest: boolean }): Promise<AxiosResponse> {
    return this.client.post('/Reviews', reviewData);
  }

  async getInstructorReviews(instructorId: number): Promise<AxiosResponse<any[]>> {
    return this.client.get(`/Reviews/instructor/${instructorId}`);
  }

  async getMyReviewForInstructor(instructorId: number): Promise<AxiosResponse> {
    return this.client.get(`/Reviews/my-review/${instructorId}`);
  }

  // Hazard Perception Test
  async getHazardClips(): Promise<AxiosResponse<any[]>> {
    return this.client.get('/HazardPerception');
  }

  async scoreHazardReaction(clipId: number, clickSecond: number): Promise<AxiosResponse> {
    return this.client.post('/HazardPerception/score', { clipId, clickSecond });
  }

  // Chat / Messaging
  async sendChatMessage(receiverId: string, messageText: string): Promise<AxiosResponse> {
    return this.client.post('/Chat/send', { receiverId, messageText });
  }

  async getChatHistory(otherUserId: string): Promise<AxiosResponse<any[]>> {
    return this.client.get(`/Chat/history/${otherUserId}`);
  }

  async getActiveChats(): Promise<AxiosResponse<any[]>> {
    return this.client.get('/Chat/active-chats');
  }

  async uploadLogo(file: File): Promise<AxiosResponse<{ path: string }>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.client.post('/upload/logo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async deleteQuote(id: number): Promise<AxiosResponse> {
    return this.client.delete(`/Quotes/${id}`);
  }

  // Testimonials
  async submitTestimonial(data: { rating: number; content: string }): Promise<AxiosResponse> {
    return this.client.post('/Testimonials', data);
  }

  async getLandingTestimonials(): Promise<AxiosResponse<any[]>> {
    return this.client.get<any[]>('/Testimonials/landing');
  }

  async getAdminTestimonials(): Promise<AxiosResponse<any[]>> {
    return this.client.get<any[]>('/Testimonials/admin');
  }

  async approveTestimonial(id: number): Promise<AxiosResponse> {
    return this.client.post(`/Testimonials/admin/${id}/approve`);
  }

  async rejectTestimonial(id: number): Promise<AxiosResponse> {
    return this.client.post(`/Testimonials/admin/${id}/reject`);
  }

  // Test Routes
  async getTestRoutes(): Promise<AxiosResponse<any[]>> {
    return this.client.get('/TestRoutes');
  }

  async getTestRouteDetails(id: number): Promise<AxiosResponse<any>> {
    return this.client.get(`/TestRoutes/${id}`);
  }

  async addTestRouteTip(id: number, data: { commentText: string; latitude: number; longitude: number; tipType?: string }): Promise<AxiosResponse<any>> {
    return this.client.post(`/TestRoutes/${id}/tips`, data);
  }

  async createTestRoute(data: { routeName: string; description?: string; centerName: string; startLocation: string; coordinatesJson?: string }): Promise<AxiosResponse<any>> {
    return this.client.post('/TestRoutes/admin', data);
  }

  async deleteTestRoute(id: number): Promise<AxiosResponse<any>> {
    return this.client.delete(`/TestRoutes/admin/${id}`);
  }

  // Theory Test Analysis (AI Study Buddy)
  async getTheoryAnalysis(): Promise<AxiosResponse<any>> {
    return this.client.get('/TheoryTest/analysis');
  }

  async startWeakestExam(): Promise<AxiosResponse<any>> {
    return this.client.get('/TheoryTest/start-weakest');
  }
}

export const api = new ApiClient();
